import { useState } from 'react';
import { PHASE_NAMES, familyOf, phasesFor, type Phase } from '@/lib/steps';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckSquare, Send, Play, Undo2, CheckCircle2, RotateCcw, UserPlus, Plus, XCircle, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Countdown from '@/components/ui/Countdown';
import PromptDialog from '@/components/ui/PromptDialog';
import FinalPickDialog from '@/components/creative/FinalPickDialog';
import ThumbnailSlot from '@/components/creative/ThumbnailSlot';
import {
  decidePhase,
  fetchCreativeTasks, fetchApprovals, fetchTaskCandidates, createTask, patchTask, submitTask, handbackTask,
  requestApproval, decideApproval, quickDecide, extendDue, grantClientAdjustments, fetchClientContacts, submitLabel, SUBMIT_STAGES, qk,
  skipPhase, markPosted, POST_PLACES,
  type Creative, type Task, type Approval, type ApproverRole } from '@/lib/queries';
import { useAuth } from '@/hooks/use-auth';
import { useCan } from '@/hooks/use-scopes';
import { useIsStaff } from '@/hooks/use-role';
import { useIsAdmin } from '@/hooks/use-scopes';
import { humanize } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useCollapsed, CollapseChevron } from '@/components/ui/CollapsibleCard';

const ROLE_LABEL: Record<ApproverRole, string> = {
  art_director_raws: 'Art director (raws)', art_director_dual: 'Art director (final)', content_creator_dual: 'Content creator (final)', client: 'Client',
};
const STATUS_CLS: Record<string, string> = {
  pending: 'bg-slate-700/40 text-slate-300', in_progress: 'bg-blue-500/15 text-blue-300', submitted: 'bg-purple-500/15 text-purple-300',
  blocked: 'bg-red-500/15 text-red-300', done: 'bg-emerald-500/15 text-emerald-300', cancelled: 'bg-slate-800/40 text-slate-500',
};
const STAGE_DESIGNATION: Record<string, (t: string) => string> = {
  brief: () => 'content_creator',
  shoot: (t) => (t === 'photo' ? 'photographer' : 'videographer'),
  raws_review: () => 'art_director',
  edit: (t) => (t === 'video' || t === 'reel' ? 'video_editor' : t === 'copy' ? 'copywriter' : 'graphic_designer'),
  dual_review: () => 'art_director',
  done: () => 'smm',
  posted: () => 'smm',
};
const PHASE_LABEL = PHASE_NAMES;
const family = familyOf;
const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Something went wrong';

/** One card for the work on a creative: who has the current step and one button to move
 *  it on; who it is waiting on; the decisions so far. Replaces the Approvals + Tasks cards. */
export default function WorkCard({ creative }: { creative: Creative }) {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const isStaff = useIsStaff();
  const canAssign = useCan('tasks.assign');
  const canDecide = useCan('approvals.decide');
  const tasks = useQuery({ queryKey: ['tasks', 'creative', creative.id], queryFn: () => fetchCreativeTasks(creative.id) });
  const approvals = useQuery({ queryKey: ['approvals', creative.id], queryFn: () => fetchApprovals(creative.id) });
  const [assignee, setAssignee] = useState('');
  const [reassign, setReassign] = useState('');
  const [decide, setDecide] = useState<{ a: Approval; decision: 'approved' | 'revision_requested' } | null>(null);
  const [pickFinal, setPickFinal] = useState(false);
  const [finals, setFinals] = useState<string[] | undefined>(undefined);
  const [rejecting, setRejecting] = useState(false);
  // a rejected final review goes back to the step the reviewer names (2026-10-01)
  const [backTo, setBackTo] = useState<Phase | null>(null);
  const [ownWork, setOwnWork] = useState(false);
  const isAdmin = useIsAdmin();   // only an owner or manager may override the maker rule
  const [extending, setExtending] = useState(false);
  const [extendUntil, setExtendUntil] = useState('');
  const [extendNote, setExtendNote] = useState('');
  const [handback, setHandback] = useState<Task | null>(null);
  const [picking, setPicking] = useState(false);
  const [phasePick, setPhasePick] = useState<Record<string, string>>({});
  const [phaseReject, setPhaseReject] = useState<Task | null>(null);

  const open = (tasks.data ?? []).filter((t) => !['done', 'cancelled'].includes(t.status));
  const all = tasks.data ?? [];
  // the edit steps this kind of work has: a static, carousel or story has the design alone (Aziz, 2026-10-04)
  const PHASES = phasesFor(creative.creative_type);
  const showPhases = creative.current_stage === 'edit' || open.some((t) => t.phase);
  const phaseTask = (p: Phase) => all.find((t) => t.stage === 'edit' && t.phase === p && t.status !== 'cancelled') ?? null;
  // The edit is one phase at a time: the open one, else the first without a done task.
  const openPhase = PHASES.find((p) => { const t = phaseTask(p); return !!t && t.status !== 'done'; }) ?? null;
  const nextPhase = openPhase ?? PHASES.find((p) => !(creative.skipped_phases ?? []).includes(p) && (!phaseTask(p) || phaseTask(p)!.status !== 'done')) ?? null;
  const donePhases = PHASES.filter((p) => phaseTask(p)?.status === 'done');
  //: the edit steps this piece has, for "send it back to"; the last one is the default
  const backChoices = PHASES.filter((p) => !!phaseTask(p));
  //: phases this piece has been told it does not need, so the flow steps over them
  const skippedPhases = PHASES.filter((p) => (creative.skipped_phases ?? []).includes(p));
  const isDirector = !!me && (me.role === 'owner' || me.role === 'manager' || (me.designations ?? []).some((d) => d === 'art_director' || d === 'creative_director'));
  const current = open.find((t) => t.stage === creative.current_stage && (!showPhases || !!t.phase))
    ?? open.find((t) => t.stage === creative.current_stage) ?? open[0] ?? null;
  const mine = !!current && current.assignee_id === me?.id;
  const round = creative.revision_round;
  const thisRound = (approvals.data ?? []).filter((a) => a.revision_round === round);
  const pending = thisRound.filter((a) => a.decision === 'pending');
  const dualDone = ['art_director_dual', 'content_creator_dual'].every((r) => thisRound.some((a) => a.approver_role === r && a.decision === 'approved'));
  const clientRow = thisRound.find((a) => a.approver_role === 'client');
  const decided = [...(approvals.data ?? [])].filter((a) => a.decision !== 'pending' && a.decision !== 'skipped').sort((a, b) => (b.decided_at ?? '').localeCompare(a.decided_at ?? '')).slice(0, 6);
  // the open task says who the work is for; the stage only when there is none (a static sent
  // back to the brief still has its design step open: the picker offered content creators)
  const designation = current?.required_designation || (STAGE_DESIGNATION[creative.current_stage] ?? (() => undefined))(creative.creative_type);
  const byDesignation = useQuery({ queryKey: ['task-candidates', creative.id, designation], queryFn: () => fetchTaskCandidates(creative.id, designation), enabled: canAssign });
  // Nobody on this clinic carries that designation: offer everyone who can work on it
  // rather than an empty list the planner cannot get past.
  const anyone = useQuery({ queryKey: ['task-candidates', creative.id, 'any'], queryFn: () => fetchTaskCandidates(creative.id), enabled: canAssign && byDesignation.isSuccess && (byDesignation.data ?? []).length === 0 });
  const people = (byDesignation.data ?? []).length ? (byDesignation.data ?? []) : (anyone.data ?? []);
  const widened = (byDesignation.data ?? []).length === 0 && people.length > 0;
  const candidates = { data: people };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tasks', 'creative', creative.id] });
    qc.invalidateQueries({ queryKey: ['tasks', 'my'] });
    qc.invalidateQueries({ queryKey: ['waiting-on-me'] });   // the decision leaves My Tasks at once
    qc.invalidateQueries({ queryKey: ['approvals', creative.id] });
    qc.invalidateQueries({ queryKey: qk.creative(creative.id) });
    qc.invalidateQueries({ queryKey: ['creatives', creative.id, 'activity'] });
    qc.invalidateQueries({ queryKey: ['track', creative.id] });
  };
  const start = useMutation({ mutationFn: (t: Task) => patchTask(t.id, { status: 'in_progress' }), onSuccess: () => { toast.success('Started'); invalidate(); }, onError: (e) => toast.error(errMsg(e)) });
  const submit = useMutation({
    mutationFn: (t: Task) => submitTask(t.id),
    onSuccess: (r) => { toast.success(`Submitted. ${r.notified} reviewer${r.notified === 1 ? '' : 's'} notified.`); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const done = useMutation({ mutationFn: (t: Task) => patchTask(t.id, { status: 'done' }), onSuccess: () => { toast.success('Done'); invalidate(); }, onError: (e) => toast.error(errMsg(e)) });
  const assign = useMutation({
    mutationFn: () => createTask({ creative_id: creative.id, stage: creative.current_stage, required_designation: designation, assignee_id: assignee }),
    onSuccess: (t) => { toast.success(`${humanize(t.stage)} assigned to ${t.assignee_name}`); setAssignee(''); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  //: an edit phase this piece does not need, and putting it back
  const skip = useMutation({
    mutationFn: ({ phase, skip: on }: { phase: Phase; skip: boolean }) => skipPhase(creative.id, phase, on),
    onSuccess: (_r, v) => {
      toast.success(v.skip
        ? `${PHASE_LABEL[family(creative.creative_type)][v.phase]} is not needed on this one`
        : `${PHASE_LABEL[family(creative.creative_type)][v.phase]} is back`);
      invalidate();
    },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not change the phase'),
  });
  const assignPhase = useMutation({
    mutationFn: ({ phase, to }: { phase: Phase; to: string }) =>
      createTask({ creative_id: creative.id, stage: 'edit', phase, required_designation: designation, assignee_id: to }),
    onSuccess: (t) => { toast.success(`${PHASE_LABEL[family(creative.creative_type)][(t.phase ?? 'rough') as Phase]} assigned to ${t.assignee_name}`); setPhasePick({}); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const movePhase = useMutation({
    mutationFn: ({ t, to }: { t: Task; to: string }) => patchTask(t.id, { assignee_id: to }),
    onSuccess: (t) => { toast.success(`Now with ${t.assignee_name}`); setPhasePick({}); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const phaseDecide = useMutation({
    mutationFn: ({ t, decision, notes, mentions }: { t: Task; decision: 'approved' | 'rejected'; notes?: string; mentions?: string[] }) => decidePhase(t.id, decision, notes, mentions),
    onSuccess: (r, v) => { toast.success(v.decision === 'approved' ? `Approved. ${r.notified ? 'The next step is handed out.' : 'Nobody is on the next step yet.'}` : 'Sent back with your note.'); setPhaseReject(null); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const move = useMutation({
    mutationFn: ({ t, to }: { t: Task; to: string }) => patchTask(t.id, { assignee_id: to }),
    onSuccess: (t) => { toast.success(`Now with ${t.assignee_name}`); setReassign(''); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const back = useMutation({
    mutationFn: ({ t, reason, mentions }: { t: Task; reason: string; mentions?: string[] }) => handbackTask(t.id, reason, mentions),
    onSuccess: () => { toast.success('Handed back. The planners are told.'); setHandback(null); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const decideMut = useMutation({
    mutationFn: ({ a, decision, notes, mentions }: { a: Approval; decision: 'approved' | 'revision_requested'; notes?: string; mentions?: string[] }) => decideApproval(a.id, decision, notes, mentions),
    onSuccess: (a) => { toast.success(a.decision === 'approved' ? 'Approved' : 'Revision requested'); setDecide(null); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  // One button each. The server works out which seat this person holds.
  const quick = useMutation({
    mutationFn: ({ decision, notes, reason, mentions, back, finals }: { decision: 'approved' | 'rejected'; notes?: string; reason?: string; mentions?: string[]; back?: Phase; finals?: string[] }) => quickDecide(creative.id, decision, notes, reason, mentions, back, finals),
    onSuccess: (r, v) => { toast.success(r.decision === 'approved' ? (v.finals ? `Approved. ${v.finals.length} file${v.finals.length === 1 ? '' : 's'} picked for the clinic.` : 'Approved') : `Rejected. Back to the ${v.back ? PHASE_LABEL[family(creative.creative_type)][v.back].toLowerCase() : 'editor'}, round ${r.revision_round}.`); setRejecting(false); setBackTo(null); setOwnWork(false); setPickFinal(false); setFinals(undefined); invalidate(); },
    onError: (e) => {
      // Separation of duties: whoever shot or edited the piece does not approve it.
      // Only an owner or manager is asked for a reason to override; everyone else is
      // told plainly who decides (the old dialog offered a button the server refused).
      if (/work you produced/i.test(errMsg(e))) { setPickFinal(false); if (isAdmin) setOwnWork(true); else toast.error(errMsg(e)); return; }
      toast.error(errMsg(e));
    },
  });
  const extend = useMutation({
    mutationFn: () => extendDue(creative.id, new Date(extendUntil).toISOString(), extendNote.trim()),
    onSuccess: () => { toast.success('Deadline extended. The team is told.'); setExtending(false); setExtendUntil(''); setExtendNote(''); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  // a decision still open on this creative keeps its buttons, whatever step it sits on
  // (MS-4-2026-009 was moved to Client review with both reviews open and lost them)
  const openReview = (approvals.data ?? []).some((a) => a.decision === 'pending' && a.approver_role !== 'client' && a.revision_round === creative.revision_round);
  // During the edit the step row (rough cut, colouring) carries the decision. The top bar
  // there used to accept the whole cut as final and skip the steps left: two Approve
  // buttons doing different things (Aziz 2026-09-30). One decision, on the step.
  const stepDecides = creative.current_stage === 'edit' && showPhases && !!nextPhase;
  const isFinalReview = thisRound.some((a) => a.decision === 'pending' && a.approver_role.endsWith('_dual')) || creative.current_stage === 'dual_review';
  // Only whoever holds the decision sees its buttons: the person named on the seat, else
  // the seat's job, plus owners and managers. The videographer was offered Approve and
  // Reject on his own raws (live check, 2026-10-01); the server refused, the screen did not.
  const SEAT_JOBS: Record<string, string[]> = { art_director_raws: ['art_director', 'creative_director'], art_director_dual: ['art_director', 'creative_director'], content_creator_dual: ['content_creator'] };
  const myJobs = new Set(me?.designations ?? []);
  const holdsSeat = (role: string, ids?: string[] | null) => isAdmin || (ids && ids.length ? !!me && ids.includes(me.id) : (SEAT_JOBS[role] ?? []).some((d) => myJobs.has(d)));
  const stageSeats = ['shoot', 'raws_review'].includes(creative.current_stage) ? ['art_director_raws'] : ['edit', 'dual_review'].includes(creative.current_stage) ? ['content_creator_dual'] : [];
  const openSeats = pending.filter((a) => a.approver_role !== 'client');
  const iDecide = openSeats.length ? openSeats.some((a) => holdsSeat(a.approver_role, a.approver_ids)) : stageSeats.some((r) => holdsSeat(r));
  const decidable = (['shoot', 'raws_review', 'edit', 'dual_review'].includes(creative.current_stage) || openReview) && !creative.client_approved_at && !stepDecides;
  const canExtend = (canDecide || canAssign) && !['done', 'posted'].includes(creative.current_stage);
  // A review step is a decision, not a task. Offering "Assign" and "Mark done" there made
  // Mahmoud create three raws review tasks and tick them off, which decided nothing
  // (MS-3-2026-003, 2026-09-30). The decision is the Approve / Reject bar.
  const reviewStep = ['raws_review', 'dual_review'].includes(creative.current_stage);
  const toClient = useMutation({
    mutationFn: (ids?: string[]) => requestApproval(creative.id, 'client', ids),
    onSuccess: () => { toast.success('Client approval requested'); setPicking(false); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const nextLabel = (t: Task) => (SUBMIT_STAGES.includes(t.stage) ? submitLabel(t.stage) : 'Mark done');
  const { open: sectionOpen, toggle: toggleSection } = useCollapsed('creative.work.open');

  return (
    <Card>
      <div className={cn('flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5', sectionOpen && 'mb-3')}>
        <button type="button" onClick={toggleSection} aria-expanded={sectionOpen}
          className="font-medium text-muted-strong text-sm inline-flex items-center gap-2 mr-2">
          <CollapseChevron open={sectionOpen} /><CheckSquare size={14} /> Work
        </button>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 ml-auto">
          {round > 0 && <span className="text-[10px] font-mono text-muted">round {round}</span>}
          {canExtend && (
            <button type="button" onClick={() => setExtending((v) => !v)} data-testid="work-extend"
              className="text-[11px] inline-flex items-center gap-1 whitespace-nowrap text-muted hover:text-accent-400">
              <CalendarClock size={12} /> Extend deadline
            </button>
          )}
          <ClientRounds creative={creative} onChanged={invalidate} />
        </div>
      </div>

      {sectionOpen && (<>
      {/* Extend the deadline: a date and a reason. */}
      {extending && (
        <div className="rounded-lg border border-slate-800 bg-bg px-3 py-3 mb-3" data-testid="work-extend-form">
          <div className="text-xs text-muted-strong mb-2">New deadline for the {humanize(creative.current_stage)} step</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input type="datetime-local" value={extendUntil} onChange={(e) => setExtendUntil(e.target.value)} data-testid="work-extend-until"
              className="h-9 px-2 rounded-lg bg-bg-card border border-slate-800 text-sm text-muted-strong" />
            <input type="text" value={extendNote} onChange={(e) => setExtendNote(e.target.value)} placeholder="Why (required)" data-testid="work-extend-note"
              className="h-9 px-2 flex-1 rounded-lg bg-bg-card border border-slate-800 text-sm text-muted-strong" />
            <Button size="sm" icon={<CalendarClock size={12} />} loading={extend.isPending} disabled={!extendUntil || extendNote.trim().length < 3}
              onClick={() => extend.mutate()} data-testid="work-extend-save">Extend</Button>
            <Button size="sm" variant="secondary" onClick={() => setExtending(false)}>Cancel</Button>
          </div>
          {creative.due_extended_until && (
            <div className="text-[11px] text-muted mt-2">Currently extended to {new Date(creative.due_extended_until).toLocaleString('en-AE')}{creative.due_extension_note ? `: ${creative.due_extension_note}` : ''}</div>
          )}
        </div>
      )}

      {/* The decision: one button each, always here for the reviewer. */}
      {isStaff && canDecide && decidable && iDecide && (
        <div className="rounded-lg border border-accent-400/30 bg-accent-600/5 px-3 py-3 mb-3 flex flex-wrap items-center gap-2" data-testid="work-decide">
          <span className="text-sm text-muted-strong">Your decision on the {
            // the open seat says what is being decided, whatever step the creative sits on
            thisRound.some((a) => a.decision === 'pending' && a.approver_role.endsWith('_dual')) || creative.current_stage === 'dual_review' ? 'final edit'
              : creative.current_stage === 'edit' ? 'cut' : 'raws'}</span>
          {creative.due_extended_until && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-muted" title={creative.due_extension_note ?? ''}>deadline extended</span>}
          <div className="ml-auto flex gap-2">
            <Button size="sm" icon={<CheckCircle2 size={12} />} loading={quick.isPending}
              onClick={() => (isFinalReview || creative.current_stage === 'edit' ? setPickFinal(true) : quick.mutate({ decision: 'approved' }))} data-testid="work-approve">Approve</Button>
            <Button size="sm" variant="secondary" icon={<XCircle size={12} />} className="text-red-300 hover:text-red-200" disabled={quick.isPending} onClick={() => setRejecting(true)} data-testid="work-reject">Reject</Button>
          </div>
        </div>
      )}

      {/* Posted: the live link (Esraa, 2026-10-25: nothing in the app took it). The posting
          step's person, owners and managers; the server holds the same line. */}
      {['done', 'posted'].includes(creative.current_stage)
        && (isAdmin || myJobs.has('smm') || open.some((t) => t.stage === 'posted' && t.assignee_id === me?.id)) && (
        <PostedLink creative={creative} onSaved={invalidate} />
      )}

      {/* Now */}
      {current && !(showPhases && current.phase) ? (
        <div className={cn('rounded-lg border px-3 py-3 mb-3', current.status === 'blocked' ? 'border-red-500/30 bg-red-500/5' : 'border-accent-400/30 bg-accent-600/5')} data-testid="work-now">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', STATUS_CLS[current.status])}>{humanize(current.status)}</span>
            <span className="font-medium text-muted-strong">{humanize(current.stage)}</span>
            <span className="text-muted">with <span className="text-muted-strong">{current.assignee_name ?? 'nobody yet'}</span>{mine ? ' (you)' : ''}</span>
            {!['done', 'cancelled'].includes(current.status) && <Countdown due={current.due_at} />}
          </div>
          {current.notes && <div className="text-xs text-muted mt-1.5 whitespace-pre-wrap">{current.notes}</div>}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {/* a step is moved along by its own person (Aziz 2026-10-25: no tampering); assigning is not doing */}
            {(mine || isAdmin || !current.assignee_id) && current.status === 'pending' && (
              <Button size="sm" icon={<Play size={12} />} onClick={() => start.mutate(current)} loading={start.isPending}>Start</Button>
            )}
            {(mine || isAdmin || !current.assignee_id) && current.status !== 'pending' && (
              reviewStep && current.stage === creative.current_stage
                ? <span className="text-xs text-muted" data-testid="work-review-hint">{isStaff && canDecide && decidable ? 'Decide it with Approve or Reject above.' : 'This step is the review decision.'}</span>
                : SUBMIT_STAGES.includes(current.stage)
                ? <Button size="sm" icon={<Send size={12} />} onClick={() => submit.mutate(current)} loading={submit.isPending} data-testid="work-submit">{nextLabel(current)}</Button>
                : <Button size="sm" icon={<CheckCircle2 size={12} />} onClick={() => done.mutate(current)} loading={done.isPending} data-testid="work-submit">Mark done</Button>
            )}
            {mine && <button className="text-xs text-muted hover:text-red-300 underline min-h-[36px] inline-flex items-center gap-1" onClick={() => setHandback(current)}><Undo2 size={11} /> Can't do it</button>}
            {canAssign && (
              <div className="ml-auto min-w-[180px]">
                <Select size="sm" value={reassign} placeholder="Reassign to..." onChange={(v) => { setReassign(v); if (v) move.mutate({ t: current, to: v }); }}
                  options={(candidates.data ?? []).filter((u) => u.id !== current.assignee_id).map((u) => ({ value: u.id, label: u.display_name, hint: `${u.open_tasks} open` }))} />
              </div>
            )}
          </div>
        </div>
      ) : reviewStep && !showPhases ? (
        pending.length === 0 && !(isStaff && canDecide && decidable) ? (
          <div className="rounded-lg border border-slate-800 bg-bg px-3 py-3 mb-3 text-sm text-muted" data-testid="work-review-hint">
            Waiting on the {creative.current_stage === 'raws_review' ? 'art director to decide the raws' : 'final review'}.
          </div>
        ) : null
      ) : !showPhases && pending.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-bg px-3 py-3 mb-3" data-testid="work-now">
          <div className="text-sm text-muted">Nobody has the <span className="text-muted-strong">{humanize(creative.current_stage)}</span> step yet.</div>
          {canAssign && creative.current_stage !== 'posted' && (
            <div className="flex flex-col sm:flex-row sm:items-end gap-2 mt-2">
              <div className="flex-1"><Select size="sm" label={`Give it to (${designation ? humanize(designation) : 'anyone'})`} value={assignee} onChange={setAssignee} placeholder="Pick a person" searchable
                options={(candidates.data ?? []).map((u) => ({ value: u.id, label: u.display_name, hint: `${u.open_tasks} open` }))} /></div>
              <Button size="sm" icon={<UserPlus size={12} />} onClick={() => assign.mutate()} disabled={!assignee || assign.isPending}>Assign</Button>
            </div>
          )}
        </div>
      ) : null}

      {/* The edit, one phase at a time: rough cut, then colouring, then final edit. Only the
          current phase is shown with its buttons; the ones before it are one line each; the
          ones after it do not exist yet and are not shown, so nobody uploads to the wrong step. */}
      {showPhases && (
        <div className="mb-3" data-testid="work-phases">
          <div className="text-[10px] uppercase tracking-wide text-muted mb-1">The edit, step by step</div>
          <div className="divide-y divide-slate-800/60">
            {skippedPhases.map((p) => (
              <div key={p} data-testid={'phase-row-' + p} data-state="skipped" className="flex flex-wrap items-center gap-2 py-1.5">
                <span className="w-2 h-2 rounded-full shrink-0 bg-slate-700" />
                <span className="text-sm text-muted min-w-[130px] line-through">{PHASE_LABEL[family(creative.creative_type)][p]}</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-slate-700/40 text-muted">not needed</span>
                {canAssign && (
                  <button className="ml-auto text-[11px] text-accent-400 hover:underline" data-testid={'phase-restore-' + p}
                    onClick={() => skip.mutate({ phase: p, skip: false })}>Bring it back</button>
                )}
              </div>
            ))}
            {donePhases.map((p) => {
              const t = phaseTask(p)!;
              const label = PHASE_LABEL[family(creative.creative_type)][p];
              return (
                <div key={p} data-testid={'phase-row-' + p} data-state="done" className="flex flex-wrap items-center gap-2 py-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0 bg-emerald-500" />
                  <span className="text-sm text-muted-strong min-w-[130px]">{label}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-emerald-500/15 text-emerald-300">{p === 'final' ? 'submitted' : 'approved'}</span>
                  <span className="text-xs text-muted">{t.assignee_name ?? ''}{t.completed_at ? ` · ${new Date(t.completed_at).toLocaleDateString('en-AE')}` : ''}</span>
                </div>
              );
            })}
            {nextPhase && (() => {
              const p = nextPhase;
              const t = phaseTask(p);
              const label = PHASE_LABEL[family(creative.creative_type)][p];
              const isMine = !!t && t.assignee_id === me?.id;
              const submitted = !!t && t.status === 'submitted';
              return (
                <div key={p} data-testid={'phase-row-' + p} data-state={t ? t.status : 'unassigned'} className="flex flex-wrap items-center gap-2 py-2 bg-accent-600/5 rounded-lg px-2">
                  <span className={cn('w-2 h-2 rounded-full shrink-0', t ? 'bg-accent-500' : 'bg-slate-700')} />
                  <span className="text-sm text-muted-strong min-w-[130px]">{label}</span>
                  {t ? (
                    <>
                      <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', STATUS_CLS[t.status])}>{submitted ? 'waiting for the art director' : humanize(t.status)}</span>
                      <span className="text-xs text-muted">{t.assignee_name ?? 'nobody'}{isMine ? ' (you)' : ''}</span>
                      {!submitted && <Countdown due={t.due_at} />}
                      {t.notes && <span className="text-[11px] text-muted italic w-full" data-testid="phase-note">{t.notes}</span>}
                      <div className="ml-auto flex items-center gap-2 flex-wrap">
                        {(isMine || isAdmin || !t.assignee_id) && t.status === 'pending' && <Button size="sm" icon={<Play size={12} />} onClick={() => start.mutate(t)} loading={start.isPending}>Start</Button>}
                        {(isMine || isAdmin || !t.assignee_id) && t.status === 'in_progress' && (
                          <Button size="sm" icon={<Send size={12} />} onClick={() => submit.mutate(t)} loading={submit.isPending} data-testid={'work-submit-' + p}>
                            {p === 'final' ? 'Submit for final approval' : 'Submit the ' + label.toLowerCase()}
                          </Button>
                        )}
                        {submitted && isStaff && canDecide && isDirector && !isMine && (
                          <>
                            <Button size="sm" icon={<CheckCircle2 size={12} />} loading={phaseDecide.isPending} onClick={() => phaseDecide.mutate({ t, decision: 'approved' })} data-testid={'phase-approve-' + p}>Approve the {label.toLowerCase()}</Button>
                            <Button size="sm" variant="secondary" icon={<XCircle size={12} />} className="text-red-300 hover:text-red-200" disabled={phaseDecide.isPending} onClick={() => setPhaseReject(t)} data-testid={'phase-reject-' + p}>Reject</Button>
                          </>
                        )}
                        {canAssign && !submitted && (
                          <div className="min-w-[150px]">
                            <Select size="sm" value={phasePick[p] ?? ''} placeholder="Reassign..." onChange={(v) => { setPhasePick({ ...phasePick, [p]: v }); if (v) movePhase.mutate({ t, to: v }); }}
                              options={(candidates.data ?? []).filter((u) => u.id !== t.assignee_id).map((u) => ({ value: u.id, label: u.display_name, hint: u.open_tasks + ' open' }))} />
                          </div>
                        )}
                        {/* handed out but not worked on: still "not needed"; the last step
                            then sends the approved work to the final review */}
                        {canAssign && !submitted && p !== 'rough' && (
                          <Button size="sm" variant="secondary" disabled={skip.isPending}
                            onClick={() => skip.mutate({ phase: p, skip: true })} data-testid={'phase-skip-' + p}>Not needed</Button>
                        )}
                      </div>
                    </>
                  ) : canAssign ? (
                    <div className="ml-auto flex items-center gap-2">
                      <div className="min-w-[170px]">
                        <Select size="sm" value={phasePick[p] ?? ''} placeholder={'Who does the ' + label.toLowerCase() + '?'} searchable onChange={(v) => setPhasePick({ ...phasePick, [p]: v })}
                          options={(candidates.data ?? []).map((u) => ({ value: u.id, label: u.display_name, hint: u.open_tasks + ' open' }))} />
                      </div>
                      <Button size="sm" icon={<UserPlus size={12} />} disabled={!phasePick[p] || assignPhase.isPending} onClick={() => assignPhase.mutate({ phase: p, to: phasePick[p] })} data-testid={'phase-assign-' + p}>Assign</Button>
                      {/* Inserts are often not wanted. Saying so beats leaving the step
                          open with nobody able to fill it. */}
                      <Button size="sm" variant="secondary" disabled={skip.isPending}
                        onClick={() => skip.mutate({ phase: p, skip: true })} data-testid={'phase-skip-' + p}>Not needed</Button>
                    </div>
                  ) : <span className="ml-auto text-xs text-muted">not assigned yet</span>}
                </div>
              );
            })()}
          </div>
          {widened && <p className="text-[10px] text-amber-300 mt-1.5">Nobody on this clinic carries that designation, so everyone who works on it is listed.</p>}
          <p className="text-[10px] text-muted mt-1.5">One step at a time. A rough cut or a colouring is submitted to the art director; once approved, the next step is handed out. The inserts and final edit go to the content creator, and once approved it is the final and goes to the client.</p>
        </div>
      )}

      <PromptDialog mentions open={!!phaseReject} title={`Reject the ${phaseReject ? PHASE_LABEL[family(creative.creative_type)][phaseReject.phase as Phase].toLowerCase() : 'cut'}`}
        message={`It goes back to ${phaseReject?.assignee_name ?? 'the editor'} with your note.`} placeholder="What has to change" confirmLabel="Send back" multiline
        onConfirm={(v, named) => { if (phaseReject) phaseDecide.mutate({ t: phaseReject, decision: 'rejected', notes: v, mentions: named }); }} onCancel={() => setPhaseReject(null)} />

      {/* Waiting on */}
      {pending.length > 0 && (
        <div className="space-y-2 mb-3">
          <div className="text-[10px] uppercase tracking-wide text-muted">Waiting on</div>
          {pending.map((a) => {
            const iCanDecide = isStaff && canDecide && a.approver_role !== 'client' && holdsSeat(a.approver_role, a.approver_ids);
            return (
              <div key={a.id} className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 flex flex-wrap items-center gap-2" data-testid="work-waiting">
                <span className="text-sm text-muted-strong">{ROLE_LABEL[a.approver_role]}</span>
                <span className="text-xs text-muted">{a.approver_role === 'client'
                  ? `Sent to ${a.approver_names?.length ? a.approver_names.join(', ') : 'every contact of the clinic'}`
                  : (a.approver_names?.length ? `${a.approver_names.join(', ')} to decide` : 'anyone who qualifies, to decide')}</span>
                {iCanDecide && <span className="ml-auto text-[11px] text-muted">decide above</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Client hand-off */}
      {isStaff && canDecide && dualDone && !clientRow && !creative.client_approved_at && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 flex flex-wrap items-center gap-2 mb-3">
          <span className="text-sm text-muted-strong">Approved by the art director and the content creator.</span>
          <div className="ml-auto"><Button size="sm" icon={<Send size={12} />} onClick={() => setPicking(true)} data-testid="work-to-client">Send to the clinic</Button></div>
        </div>
      )}
      {creative.client_approved_at && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-300 mb-3">Client approved on {new Date(creative.client_approved_at).toLocaleDateString('en-AE')}. Final.</div>
      )}

      {/* Decisions */}
      {decided.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted mb-1">Decisions</div>
          <div className="divide-y divide-slate-800/60">
            {decided.map((a) => (
              <div key={a.id} className="py-1.5 text-xs flex flex-wrap items-center gap-x-2">
                <span className={cn('px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide', a.decision === 'approved' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300')}>{a.decision === 'approved' ? 'approved' : 'changes'}</span>
                <span className="text-muted-strong">{ROLE_LABEL[a.approver_role]}</span>
                <span className="text-muted">{a.approver_name ? `by ${a.approver_name}` : ''}{a.decided_at ? ` · ${new Date(a.decided_at).toLocaleDateString('en-AE')}` : ''}{a.revision_round ? ` · round ${a.revision_round}` : ''}</span>
                {a.notes && <span className="text-muted italic w-full">"{a.notes}"</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {!current && pending.length === 0 && decided.length === 0 && !canAssign && <div className="text-xs text-muted">Nothing to do here yet.</div>}

      </>)}

      {picking && <ClientApproverPicker clientId={creative.client_id} busy={toClient.isPending} onCancel={() => setPicking(false)} onSend={(ids) => toClient.mutate(ids)} />}

      <PromptDialog mentions open={!!decide} title="What needs to change?" placeholder="Be specific: shot, timing, copy..." confirmLabel="Send back" multiline
        onConfirm={(v, named) => { if (decide) decideMut.mutate({ a: decide.a, decision: decide.decision, notes: v, mentions: named }); }} onCancel={() => setDecide(null)} />
      <PromptDialog mentions open={ownWork} title="You shot or edited this work" message="Approving it needs a written reason; the reason is recorded." placeholder="Reason" confirmLabel="Approve anyway"
        onConfirm={(v, named) => { if (v.trim().length < 3) { toast.error('A reason is required'); return; } quick.mutate({ decision: 'approved', reason: v.trim(), mentions: named, finals }); }} onCancel={() => setOwnWork(false)} />
      {/* approving the final review: only the ticked files go to the clinic (FinalPickDialog) */}
      {pickFinal && <FinalPickDialog creativeId={creative.id} busy={quick.isPending} onCancel={() => setPickFinal(false)}
        onConfirm={(ids) => { setFinals(ids); quick.mutate({ decision: 'approved', finals: ids }); }} />}
      <PromptDialog mentions open={rejecting} title="Reject: what needs to change?"
        message={isFinalReview && backChoices.length > 0 ? (
          <div data-testid="reject-back-to">
            <div className="mb-1.5">Send it back to:</div>
            <div className="flex flex-wrap gap-2">
              {backChoices.map((p) => {
                const on = (backTo ?? backChoices[backChoices.length - 1]) === p;
                const who = phaseTask(p)?.assignee_name;
                return (
                  <button key={p} type="button" onClick={() => setBackTo(p)} aria-pressed={on} data-testid={'reject-back-' + p}
                    className={cn('rounded-lg border px-2.5 py-1.5 text-xs text-left', on ? 'border-red-400/60 bg-red-500/10 text-muted-strong' : 'border-slate-800 text-muted')}>
                    {PHASE_LABEL[family(creative.creative_type)][p]}{who ? <span className="block text-[10px] text-muted">{who}</span> : null}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] mt-1.5">That step's editor gets your note. The steps after it are done again once it is approved.</div>
          </div>
        ) : 'The editor gets this note and the work comes back to them.'}
        placeholder="Be specific: shot, timing, copy..." confirmLabel="Reject" multiline
        onConfirm={(v, named) => { if (v.trim().length < 3) { toast.error('Say what needs to change'); return; } quick.mutate({ decision: 'rejected', notes: v.trim(), mentions: named, back: isFinalReview && backChoices.length ? (backTo ?? backChoices[backChoices.length - 1]) : undefined }); }}
        onCancel={() => { setRejecting(false); setBackTo(null); }} />
      <PromptDialog mentions open={!!handback} title="Hand this step back" message="Say why; the art director, content creator and admins are told and can move it to someone else." placeholder="Reason" confirmLabel="Hand back" multiline
        onConfirm={(v, named) => { if (handback) back.mutate({ t: handback, reason: v, mentions: named }); }} onCancel={() => setHandback(null)} />
    </Card>
  );
}

function ClientRounds({ creative, onChanged }: { creative: Creative; onChanged: () => void }) {
  const canGrant = useCan('approvals.override');
  const [ask, setAsk] = useState(false);
  const grant = useMutation({
    mutationFn: ({ reason, mentions }: { reason: string; mentions?: string[] }) => grantClientAdjustments(creative.id, 1, reason, mentions),
    onSuccess: () => { setAsk(false); toast.success('One more adjustment round granted'); onChanged(); },
  });
  const used = creative.client_adjustments_used ?? 0;
  const extra = creative.client_adjustments_extra ?? 0;
  return (
    <span className="flex items-center gap-2 text-[11px]" data-testid="client-rounds">
      {creative.client_approved_at ? (
        <span className="font-mono px-1.5 py-0.5 rounded border text-emerald-300 border-emerald-500/30 bg-emerald-500/10">client approved</span>
      ) : (
        <span className="text-muted">client rounds used: <b className="text-muted-strong">{used}</b>{extra ? ` (+${extra} extra)` : ''}</span>
      )}
      {canGrant && !creative.client_approved_at && (
        <button onClick={() => setAsk(true)} className="text-accent-400 hover:underline">+1 round</button>
      )}
      <PromptDialog
        open={ask}
        title="Grant one extra adjustment round"
        message="The client's included number stays the same; this creative gets one more. Say why; it is recorded."
        mentions
        placeholder="Reason"
        confirmLabel="Grant"
        onConfirm={(v, named) => { if (v.trim().length >= 3) grant.mutate({ reason: v.trim(), mentions: named }); else toast.error('A reason is required'); }}
        onCancel={() => setAsk(false)}
      />
    </span>
  );
}


/** Who at the clinic must sign this off. All contacts are ticked by default. */
function ClientApproverPicker({ clientId, busy, onCancel, onSend }: {
  clientId: string; busy: boolean; onCancel: () => void; onSend: (ids: string[] | undefined) => void;
}) {
  const contacts = useQuery({ queryKey: qk.clientContacts(clientId), queryFn: () => fetchClientContacts(clientId) });
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const all = contacts.data ?? [];
  const set = chosen ?? new Set(all.map((c) => c.id));
  const toggle = (id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setChosen(next);
  };
  const everyone = all.length > 0 && set.size === all.length;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-bg-card p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-medium text-muted-strong text-sm">Send for client approval</h3>
        <p className="text-xs text-muted mt-1 mb-3">Tick who must approve. Everyone else at the clinic can still view and comment.</p>
        {contacts.isLoading && <div className="text-xs text-muted">Loading contacts...</div>}
        {!contacts.isLoading && all.length === 0 && (
          <div className="text-xs text-amber-300">
            This clinic has no portal contacts yet. <Link to={`/clients/${clientId}`} className="underline">Add one on the client page</Link> first.
          </div>
        )}
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {all.map((c) => (
            <label key={c.id} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-800/50 cursor-pointer">
              <input type="checkbox" checked={set.has(c.id)} onChange={() => toggle(c.id)} className="accent-accent-500" />
              <span className="text-sm text-muted-strong">{c.display_name}</span>
              <span className="text-[11px] text-muted truncate">{c.email}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 mt-4">
          <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            size="sm"
            icon={<Plus size={12} />}
            disabled={busy || all.length === 0 || set.size === 0}
            loading={busy}
            onClick={() => onSend(everyone ? undefined : Array.from(set))}
          >
            {everyone ? `Send to all ${all.length}` : `Send to ${set.size} of ${all.length}`}
          </Button>
        </div>
      </div>
    </div>
  );
}


function PostedLink({ creative, onSaved }: { creative: Creative; onSaved: () => void }) {
  // every place it went live (Aziz 2026-10-25: "a plus button, a drop down list: TikTok, stories, whatever")
  const saved = creative.posted_links?.length ? creative.posted_links
    : creative.posted_url ? [{ platform: 'other', url: creative.posted_url }] : [];
  const [rows, setRows] = useState<{ platform: string; url: string }[]>(
    saved.length ? saved.map((l) => ({ platform: l.platform, url: l.url })) : [{ platform: 'instagram_reel', url: '' }]);
  const set = (i: number, patch: Partial<{ platform: string; url: string }>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const filled = rows.filter((r) => r.url.trim());
  const same = JSON.stringify(filled.map((r) => [r.platform, r.url.trim()])) === JSON.stringify(saved.map((l) => [l.platform, l.url]));
  const save = useMutation({
    mutationFn: () => markPosted(creative.id, filled.map((r) => ({ platform: r.platform, url: r.url.trim() }))),
    onSuccess: () => { toast.success(saved.length ? 'Links saved' : 'Marked posted'); onSaved(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-3 mb-3" data-testid="posted-link">
      {(creative.client_approved_at || creative.current_stage === 'posted') && <ThumbnailSlot creative={creative} />}
      <div className="text-sm text-muted-strong mb-2">{saved.length ? 'Posted. Where it is live:' : 'Posted it? Add every place it went live:'}</div>
      <div className="flex flex-col gap-2">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid="posted-row">
            <Select size="sm" className="w-[170px]" value={r.platform} onChange={(v) => set(i, { platform: v })} options={POST_PLACES} data-testid="posted-platform" />
            <input value={r.url} onChange={(e) => set(i, { url: e.target.value })} placeholder="https://..." dir="ltr" inputMode="url"
              className="flex-1 min-w-[200px] h-9 rounded-lg border border-line bg-bg px-3 text-sm text-muted-strong" data-testid="posted-url" />
            {rows.length > 1 && (
              <button className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-muted hover:text-red-300" aria-label="Remove this link"
                onClick={() => setRows((x) => x.filter((_, j) => j !== i))} data-testid="posted-remove"><XCircle size={14} /></button>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Button size="sm" variant="secondary" icon={<Plus size={12} />} onClick={() => setRows((x) => [...x, { platform: 'tiktok', url: '' }])}
          disabled={rows.length >= 20} data-testid="posted-add">Add link</Button>
        <Button size="sm" icon={<CheckCircle2 size={12} />} onClick={() => save.mutate()} loading={save.isPending}
          disabled={!filled.length || same} data-testid="posted-save">{saved.length ? 'Save links' : 'Mark posted'}</Button>
      </div>
    </div>
  );
}
